import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getRuntimeProjects, getRuntimeTasks } from '../lib/api';
import { Briefcase, Plus, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const getExecutionAgentMetadata = (project) => {
  const metadata = project?.metadata || {};
  if (metadata?.agent_kind === 'agent_employee') return metadata;
  if (metadata?.product_type === 'execution_agent') return metadata;
  return null;
};

const ExecutionAgentsPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, tasksRes] = await Promise.all([
          getRuntimeProjects(),
          getRuntimeTasks(),
        ]);
        setProjects(projectsRes.data || []);
        setTasks(tasksRes.data || []);
      } catch (err) {
        console.error(err);
        setError('Failed to load agent employees');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const executionAgents = useMemo(() => (
    projects
      .map((project) => {
        const meta = getExecutionAgentMetadata(project);
        if (!meta) return null;
        const relatedTasks = tasks.filter((task) => task.project_id === project.id);
        const runtimeProfile = meta.runtime_profile || {};
        return {
          project,
          meta,
          runtimeProfile,
          agentProfile: runtimeProfile.agent_profile || {},
          runtimePolicy: runtimeProfile.runtime_policy || {},
          relatedTasks,
          starterTask: relatedTasks[0] || null,
        };
      })
      .filter(Boolean)
  ), [projects, tasks]);

  return (
    <Layout>
      <div className="p-8 space-y-6" data-testid="execution-agents-page">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight font-['Outfit']">Agent Employees</h1>
            <p className="text-muted-foreground mt-1">Open an agent employee to operate it directly, review work in progress, and chat with it from the app.</p>
          </div>
          <div className="flex gap-3">
            <Button className="btn-gradient text-white rounded-lg gap-2" onClick={() => navigate('/agent-employees/new')}>
              <Plus className="w-4 h-4" />
              Add agent employee
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : error ? (
          <div className="apollo-card p-4 text-sm text-red-400">{error}</div>
        ) : executionAgents.length === 0 ? (
          <div className="apollo-card p-12 text-center">
            <Briefcase className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2 font-['Outfit']">No agent employees yet</h2>
            <p className="text-muted-foreground mb-6">Create your first execution agent to scaffold a runtime project and starter task.</p>
            <Button className="btn-gradient text-white rounded-lg gap-2" onClick={() => navigate('/agent-employees/new')}>
              <Plus className="w-4 h-4" />
              Create agent employee
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {executionAgents.map(({ project, runtimeProfile, agentProfile, runtimePolicy, relatedTasks, starterTask }) => (
              <Card key={project.id} className="apollo-card">
                <CardHeader className="space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle className="text-xl">{runtimeProfile.name || project.title}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">{runtimeProfile.role || 'Agent employee'}</p>
                    </div>
                    <Badge variant="outline">{project.status || 'active'}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground line-clamp-3">{runtimeProfile.mission || project.description || 'No mission recorded yet.'}</p>

                  {agentProfile.identity?.identity_summary ? (
                    <div className="rounded-xl border border-border/70 p-4 text-sm">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">Identity</div>
                      <div className="mt-2 text-muted-foreground line-clamp-2">{agentProfile.identity.identity_summary}</div>
                    </div>
                  ) : null}

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-xl border border-border/70 p-3">
                      <div className="text-muted-foreground text-xs uppercase tracking-wide">Open tasks</div>
                      <div className="text-xl font-semibold mt-2">{relatedTasks.length}</div>
                    </div>
                    <div className="rounded-xl border border-border/70 p-3">
                      <div className="text-muted-foreground text-xs uppercase tracking-wide">Current work</div>
                      <div className="text-sm font-semibold mt-2 line-clamp-2">{starterTask?.title || 'Not launched yet'}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
                    <Link to={`/agent-employees/${project.id}`} className="text-sm text-primary hover:text-[#EC4899] transition-colors inline-flex items-center gap-1">
                      Open employee view <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/agent-employees/${project.id}/edit`}>Configure</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ExecutionAgentsPage;
